import { useState, useRef } from "react";
import { Modal, Button, Radio, message } from "antd";
import {
  QrcodeOutlined,
  MailOutlined,
  MobileOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import apiClient from "../services/apiClient";
// Import 2FA method images
import emailIcon from "../assets/img/2fa_email.png";
import phoneIcon from "../assets/img/2fa_phone.png";
import googleIcon from "../assets/img/2fa_google.png";
import microsoftIcon from "../assets/img/2fa_Microsoft.png";
const methodIcons = {
  google: <img src={googleIcon} alt="Google Authenticator" style={{ width: 24, height: 24 }} />,
  microsoft: <img src={microsoftIcon} alt="Microsoft Authenticator" style={{ width: 24, height: 24 }} />,
  email: <img src={emailIcon} alt="Email OTP" style={{ width: 24, height: 24 }} />,
  phone: <img src={phoneIcon} alt="Phone OTP" style={{ width: 24, height: 24 }} />,
};

const methodTitles = {
  google: "Enter Google Authenticator Code",
  microsoft: "Enter Microsoft Authenticator Code",
  email: "Enter Email OTP",
  phone: "Enter SMS OTP",
};

const methodNames = {
  google: "Google Authenticator",
  microsoft: "MS Authenticator",
  email: "Email OTP",
  phone: "Phone OTP",
};

const methodDescriptions = {
  google: "Open your Google Authenticator app and enter the 6-digit code.",
  microsoft: "Open your Microsoft Authenticator app and enter the 6-digit code.",
  email: "Check your email for a one-time password (OTP) and enter it below.",
  phone:
    "Check your SMS messages for a one-time password (OTP) and enter it below.",
};

const getMethodIcon = (code) => methodIcons[code] || <QrcodeOutlined />;
const getMethodTitle = (code) =>
  methodTitles[code] || "Enter Verification Code";
const getMethodDescription = (code) =>
  methodDescriptions[code] || "Enter the code you received.";

function OtpInput({ value, onChange, length = 6 }) {
  const inputs = useRef([]);
  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    let newValue = value.split("");
    if (val) {
      newValue[idx] = val[val.length - 1];
      onChange(newValue.join("").slice(0, length));
      if (idx < length - 1) {
        inputs.current[idx + 1]?.focus();
      }
    } else {
      // If input is cleared, clear this box only
      newValue[idx] = "";
      onChange(newValue.join("").slice(0, length));
    }
  };
  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      let newValue = value.split("");
      if (newValue[idx]) {
        // Clear current box
        newValue[idx] = "";
        onChange(newValue.join("").slice(0, length));
      } else if (idx > 0) {
        // Move focus left
        inputs.current[idx - 1]?.focus();
        let prevValue = value.split("");
        prevValue[idx - 1] = "";
        onChange(prevValue.join("").slice(0, length));
        e.preventDefault();
      }
    }
  };
  const handlePaste = (e) => {
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (paste) {
      onChange(paste);
      setTimeout(() => {
        if (inputs.current[paste.length - 1])
          inputs.current[paste.length - 1].focus();
      }, 0);
    }
    e.preventDefault();
  };
  return (
    <div
      style={{ display: "flex", gap: 8, justifyContent: "center" }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[idx] || ""}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          style={{
            width: 40,
            height: 48,
            fontSize: 24,
            textAlign: "center",
            border: "1.5px solid #d9d9d9",
            borderRadius: 8,
            outline: "none",
            fontFamily: "Inter, sans-serif",
            background: "#f7f8fa",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            transition: "border 0.2s",
          }}
          autoFocus={idx === 0}
        />
      ))}
    </div>
  );
}

const TwoFactorModal = ({
  visible,
  onCancel,
  email,
  sessionId,
  availableMethods = [],
  onSuccess,
}) => {
  const [selectedMethod, setSelectedMethod] = useState(
    availableMethods.length === 1 ? availableMethods[0].methodCode : null
  );
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      message.error("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const baseUrl =
        process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";
      const payload = {
        email,
        code,
        sessionId,
      };
      if (availableMethods.length > 1) {
        payload.method = selectedMethod;
      }

      const response = await apiClient.post(`${baseUrl}/2fa/login`, payload);

      if (response.data.success && response.data.data) {
        const responseData = response.data.data;
        message.success("2FA verification successful!");
        // Call onSuccess callback with the response data
        if (onSuccess) {
          onSuccess(responseData);
        }
      } else {
        // Handle API failure response
        const errorMessage = response.data.error || response.data.message || "Invalid code or server error.";
        message.error(errorMessage);
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const methodOptions = availableMethods.map((m) => ({
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {getMethodIcon(m.methodCode)} {m.methodName}
      </span>
    ),
    value: m.methodCode,
  }));

  const currentMethod =
    selectedMethod ||
    (availableMethods.length === 1 ? availableMethods[0].methodCode : null);

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={availableMethods.length > 1 ? 900 : 400}
      centered
      bodyStyle={{ padding: 0, fontFamily: "Inter, sans-serif" }}
      style={{ top: 40 }}
      destroyOnClose
    >
      <div
        style={{
          display: "flex",
          flexDirection: availableMethods.length > 1 ? "row" : "column",
          position: "relative"
        }}
      >
        {availableMethods.length > 1 && (
          <div
            style={{
              flex: 1,
              background: "#fff",
              padding: 48,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
            }}
          >
            <h2 style={{ fontWeight: 800, fontSize: 26, marginBottom: 8, color: "#000", textAlign: "center" }}>
              SELECT 2FA METHOD
            </h2>
            <p style={{ fontSize: 15, marginBottom: 24, color: "#888", textAlign: "center" }}>
              Select your preferred 2FA method
            </p>

            {/* Method Selection Cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: availableMethods.length === 1 ? "1fr" :
                availableMethods.length === 2 ? "1fr" :
                  availableMethods.length === 3 ? "1fr 1fr" : "1fr 1fr",
              gridTemplateRows: availableMethods.length === 1 ? "1fr" :
                availableMethods.length === 2 ? "1fr 1fr" :
                  availableMethods.length === 3 ? "auto auto" : "1fr 1fr",
              gap: "16px",
              width: "100%"
            }}>
              {availableMethods.map((method, index) => {
                // Determine grid position based on method count and index
                let gridColumn = "auto";
                let gridRow = "auto";

                if (availableMethods.length === 3) {
                  if (index === 2) {
                    // Third method spans both columns in second row
                    gridColumn = "1 / -1";
                    gridRow = "2";
                  }
                }

                return (
                  <div
                    key={method.methodCode}
                    onClick={() => {
                      setSelectedMethod(method.methodCode);
                      setCode("");
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px",
                      border: selectedMethod === method.methodCode ? "2px solid #52c41a" : "1px solid #d9d9d9",
                      borderRadius: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: selectedMethod === method.methodCode ? "#f6ffed" : "#fff",
                      minHeight: "80px",
                      gridColumn: gridColumn,
                      gridRow: gridRow,
                      width: "100%"
                    }}
                  >
                    <div style={{ marginBottom: "12px" }}>
                      {methodIcons[method.methodCode]}
                    </div>
                    <span style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#000",
                      textAlign: "center"
                    }}>
                      {methodNames[method.methodCode] || method.methodName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Horizontal Divider for more than 2 methods */}
        {availableMethods.length > 2 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              width: "1px",
              backgroundColor: "#e8e8e8",
              zIndex: 1
            }}
          />
        )}

        <div
          style={{
            flex: 1,
            padding: 32,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            // background: availableMethods.length > 1 ? "#f7f8fa" : "#f7f8fa",
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
          }}
        >
          <div style={{ marginBottom: 18, textAlign: "center" }}>
            <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 6, textAlign: "center" }}>
              {getMethodTitle(currentMethod)}
            </h2>
            <div style={{ color: "#888", fontSize: 15, marginBottom: 10, textAlign: "center" }}>
              {getMethodDescription(currentMethod)}
            </div>
          </div>
          <div className="verification-input-container" style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <input
              type="password"
              className="verification-input-single"
              placeholder="* * * * * *"
              value={code}
              onChange={(e) =>
                setCode(
                  e.target.value.replace(/\D/g, "").slice(0, 6)
                )
              }
              maxLength={6}
              autoFocus
              style={{
                outline: "none",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                textAlign: "center"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#d9d9d9";
                e.target.style.boxShadow = "none";
                e.target.style.outline = "none";
              }}
            />
          </div>
          <Button
            type="primary"
            loading={loading}
            onClick={handleVerify}
            disabled={availableMethods.length > 1 && !selectedMethod}
            style={{
              fontWeight: 700,
              fontSize: 16,
              marginTop: 24,
              background: "#6A72D8",
              borderColor: "#6A72D8",
              height: "50px",
              width: "300px",
              borderRadius: "25px",
              border: "none",
              boxShadow: "0 2px 8px rgba(106, 114, 216, 0.3)",
              alignSelf: "center"
            }}
          >
            Verify
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TwoFactorModal;
